/**
 * Utility functions for sorting data throughout the application
 */

/**
 * Performs a natural sort on array elements, properly handling numeric values
 * @param {Array} array - The array to sort
 * @param {string|Function} key - Property name to sort by or accessor function
 * @param {boolean} [ascending=true] - Sort in ascending order if true, descending if false
 * @returns {Array} - Sorted array (doesn't modify the original)
 */
export const naturalSort = (array, key, ascending = true) => {
  const direction = ascending ? 1 : -1;
  
  return [...array].sort((a, b) => {
    // Get the values to compare based on key
    const aValue = typeof key === 'function' ? key(a) : (a[key] || '');
    const bValue = typeof key === 'function' ? key(b) : (b[key] || '');
    
    // Handle null, undefined, and empty values
    if (!aValue && aValue !== 0) return direction;
    if (!bValue && bValue !== 0) return -direction;
    
    // Convert to strings for comparison
    const aString = String(aValue);
    const bString = String(bValue);
    
    // Extract any numeric components
    const aMatch = aString.match(/\d+/);
    const bMatch = bString.match(/\d+/);
    
    // If both values have numeric parts
    if (aMatch && bMatch) {
      const aNum = parseInt(aMatch[0], 10);
      const bNum = parseInt(bMatch[0], 10);
      
      // If the numbers are different, sort by them
      if (aNum !== bNum) {
        return direction * (aNum - bNum);
      }
    }
    
    // Fallback to alphabetical sorting
    return direction * aString.localeCompare(bString);
  });
};

/**
 * Performs an alphabetical sort on array elements
 * @param {Array} array - The array to sort
 * @param {string|Function} key - Property name to sort by or accessor function
 * @param {boolean} [ascending=true] - Sort in ascending order if true, descending if false
 * @returns {Array} - Sorted array (doesn't modify the original)
 */
export const alphabeticalSort = (array, key, ascending = true) => {
  const direction = ascending ? 1 : -1;
  
  return [...array].sort((a, b) => {
    // Get the values to compare based on key
    const aValue = typeof key === 'function' ? key(a) : (a[key] || '');
    const bValue = typeof key === 'function' ? key(b) : (b[key] || '');
    
    // Handle null, undefined, and empty values
    if (!aValue && aValue !== 0) return direction;
    if (!bValue && bValue !== 0) return -direction;
    
    // Convert to strings and compare
    return direction * String(aValue).localeCompare(String(bValue));
  });
};

/**
 * Performs a numerical sort on array elements
 * @param {Array} array - The array to sort
 * @param {string|Function} key - Property name to sort by or accessor function
 * @param {boolean} [ascending=true] - Sort in ascending order if true, descending if false
 * @returns {Array} - Sorted array (doesn't modify the original)
 */
export const numericalSort = (array, key, ascending = true) => {
  const direction = ascending ? 1 : -1;
  
  return [...array].sort((a, b) => {
    // Get the values to compare based on key
    let aValue = typeof key === 'function' ? key(a) : a[key];
    let bValue = typeof key === 'function' ? key(b) : b[key];
    
    // Convert to numbers, using 0 for null/undefined/NaN
    aValue = aValue === null || aValue === undefined ? 0 : Number(aValue);
    bValue = bValue === null || bValue === undefined ? 0 : Number(bValue);
    
    if (isNaN(aValue)) aValue = 0;
    if (isNaN(bValue)) bValue = 0;
    
    return direction * (aValue - bValue);
  });
};

/**
 * Performs a date sort on array elements
 * @param {Array} array - The array to sort
 * @param {string|Function} key - Property name to sort by or accessor function
 * @param {boolean} [ascending=true] - Sort in ascending order if true, descending if false
 * @returns {Array} - Sorted array (doesn't modify the original)
 */
export const dateSort = (array, key, ascending = true) => {
  const direction = ascending ? 1 : -1;
  
  return [...array].sort((a, b) => {
    // Get the values to compare based on key
    const aValue = typeof key === 'function' ? key(a) : a[key];
    const bValue = typeof key === 'function' ? key(b) : b[key];
    
    // Handle null or undefined dates
    if (!aValue) return direction;
    if (!bValue) return -direction;
    
    // Convert to dates
    const aDate = new Date(aValue);
    const bDate = new Date(bValue);
    
    // Handle invalid dates
    if (isNaN(aDate.getTime())) return direction;
    if (isNaN(bDate.getTime())) return -direction;
    
    return direction * (aDate.getTime() - bDate.getTime());
  });
}; 